import React, { useState, useEffect } from 'react';
import { useHistory, Redirect, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, TextField, FormControl, Typography, Box, Divider, Tooltip,
  InputLabel, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Grid, Hidden, LinearProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'material-ui-snackbar-provider';
import SMSPreview from '../sms/SMSPreview';
import { placeholders } from '../placeholders';
import { useSelector } from 'react-redux';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from 'ckeditor5-custom-build';

import {
  useGetSMSTemplatesByIdQuery,
  useAddSMSTemplateMutation,
  useUpdateSMSTemplateMutation,
  useDeleteSMSTemplateMutation } from '../../apis/backend';


const useStyles = makeStyles(theme => ({
  root: {
    fontSize: 18,
  },
  formContainer: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
    },
    'flex' : 1,
  },
  buttonContainer : {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    '& .MuiButton-root': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1)
    }
  },
  previewSeparator: {
    marginLeft: theme.spacing(5),
    marginRight: theme.spacing(5)
  },
  previewContainer : {
    'flex' : 1,
  },
  actionButtons: {
    whiteSpace: 'nowrap',
    '& .MuiButton-root': {
      marginRight: 10,
    }
  }
}));

const SMSForm = () => {
  const [smsTemplateForm, setSMStemplateForm] = useState({ title: '', message_content: '' });
  const [editorData, setEditorData] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [skipGetSMSTemplateById, setSkipGetSMSTemplateById] = useState(true);
  const [addSMSTemplate, addResult] = useAddSMSTemplateMutation(); // eslint-disable-line no-unused-vars
  const [updateSMSTemplate, updateResult] = useUpdateSMSTemplateMutation(); // eslint-disable-line no-unused-vars
  const [deleteSMSTemplate, deleteResult] = useDeleteSMSTemplateMutation(); // eslint-disable-line no-unused-vars

  const [editor, setEditor] = useState();
  const [messageInPlainText, setMessageInPlainText] = useState();

  const chainId = useSelector(state => state.doris.selectedChain);
  const siteId = useSelector(state => state.doris.selectedSite);

  const { id } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const snackbar = useSnackbar();
  const inputRef = React.useRef();
  const { t } = useTranslation();

  const { data: smsTemplate, isLoading, error } = useGetSMSTemplatesByIdQuery(id, { skip: skipGetSMSTemplateById });

  useEffect(() => {
    const chainAndTemplate = smsTemplate && chainId && siteId;
    if (!chainAndTemplate) return;
    const templateBelongsToChain = smsTemplate.chain_id === parseInt(chainId);
    const templateBelongsToSite = smsTemplate.site_id === parseInt(siteId);
    if (!templateBelongsToChain || !templateBelongsToSite) history.push('/templates');
  }, [chainId, siteId]);

  useEffect(() => {
    if (!id) return;
    setSkipGetSMSTemplateById(false);
  }, [id]);

  useEffect(() => {
    setSMStemplateForm(smsTemplate);
    setEditorData(smsTemplate?.message_content);
  }, [smsTemplate]);

  useEffect(() => {
    setSMStemplateForm({ ...smsTemplateForm, message_content: editorData });
  }, [editorData]);

  useEffect(() => {
    if (!editor) return;
    const dataInPlainText = viewToPlainText(editor.editing.view.document.getRoot());
    setMessageInPlainText(dataInPlainText);
  }, [smsTemplateForm])

  const handleInputChange = event => {
    setSMStemplateForm({ ...smsTemplateForm, [event.target.name]: event.target.value });
  };

  const handleSaveButton = async (event) => {
    if (event.currentTarget.form.reportValidity()) {
      const templateIsNew = parseInt(id);
      try {
        if (!templateIsNew) {
          const result = await addSMSTemplate({ payload: { ...smsTemplateForm, message_content: messageInPlainText }, chainId, siteId }).unwrap(); // eslint-disable-line no-unused-vars
          snackbar.showMessage(t('sms-template-create-success'));
        } else {
          const result = await updateSMSTemplate({ ...smsTemplate, ...smsTemplateForm, message_content: messageInPlainText }).unwrap(); // eslint-disable-line no-unused-vars
          snackbar.showMessage(t('sms-template-update-success'));
        }
        history.push({ pathname: '/templates/sms' });
      } catch (error) {
        templateIsNew
          ? snackbar.showMessage(t('sms-template-create-fail'), 'error')
          : snackbar.showMessage(t('sms-template-update-fail'), 'error');
      }
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = async (confirmDelete) => {
    setOpenDialog(false);
    if (confirmDelete) {
      try {
        await deleteSMSTemplate(id).unwrap();
        snackbar.showMessage(t('sms-template-delete-success'));
        history.push({pathname: '/templates/sms'});
      } catch (err) {
        snackbar.showMessage(t('sms-template-delete-fail'), 'error');
      }
    }
  };

  const editorConfig = {
    toolbar: [
      'placeholder:registerNumber',
      'placeholder:inspectionDueDate',
      'placeholder:customerName',
      'placeholder:campaignCode',
      'placeholder:campaignEndDate',
      'placeholder:campaignPrice',
      'placeholder:campaignOnlinePrice',
      'placeholder:reservationLink'
    ],
  };

  const smsForm = (
    <div className={classes.root}>
      <Box mb={3}>
        <Typography variant="h4">
          { smsTemplate
            ? smsTemplate.title
            : t('add-sms-template')
          }
        </Typography>
        <Divider />
        { isLoading && <LinearProgress />}
      </Box>

      <Grid container>
        <Grid xs={12} lg={6} item>

          <form className={classes.formContainer} autoComplete="off">
            <FormControl fullWidth={true} margin='none'>
              <TextField
                margin="dense"
                required
                variant="outlined"
                name="title"
                label={t('title')}
                value={smsTemplateForm?.title ? smsTemplateForm.title : ''}
                onChange={handleInputChange} />
            </FormControl>

            <Box my={1} p={1}>
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={editorData || ''}
                onReady={editor => {
                  setEditor(editor);
                  editor.setData(smsTemplateForm?.message_content || '');
                }}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setEditorData(data);
                }} />
            </Box>




            <Hidden lgUp>
              <Box className={classes.previewContainer} p={2}>
                <SMSPreview
                  className={classes.previewContainer}
                  previewText={messageInPlainText} />
              </Box>
            </Hidden>

            <Box className={classes.actionButtons} p={1}>
              <Button
                className={classes.button}
                variant="contained"
                color="primary"
                onClick={(event) => handleSaveButton(event)}>
                {t('save-exit')}
              </Button>

              <Button
                className={classes.button}
                variant="outlined"
                color="primary"
                onClick={() => history.push({ pathname: '/templates/sms' })
                }>{t('cancel')}
              </Button>

              {
                smsTemplate && (
                  <Button onClick={() => handleOpenDialog()}
                    color="primary"
                    className={classes.button}
                    variant="outlined" >
                    {t('delete')} {t('SMS-template')}
                  </Button>
                )
              }
            </Box>
          </form>

        </Grid>

        <Hidden mdDown>
          <Grid md={6} item>
            <Box display="flex" height="100%" pr={2}>
              <Divider orientation="vertical" flexItem className={classes.previewSeparator} />
              <Box className={classes.previewContainer}>
                <SMSPreview
                  className={classes.previewContainer}
                  previewText={messageInPlainText} />
              </Box>
            </Box>
          </Grid>
        </Hidden>
      </Grid>
    </div>
  );

  if (error) return <Redirect to="/page-not-found" />;

  return (
    <>
      <Box p={4}>{smsForm}</Box>

      <Dialog
        open={openDialog}
        onClose={() => handleCloseDialog(false)}>
        <DialogTitle
          id="alert-dialog-title">
          {t('delete-sms-template')}
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            id="alert-dialog-description">
            {t('delete-alert')}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => handleCloseDialog(false)}
            color="primary">
            {t('cancel')}
          </Button>

          <Button
            onClick={() => handleCloseDialog(true)}
            color="primary"
            autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


const smallPaddingElements = [ 'figcaption', 'li' ];

/**
 * Converts {@link module:engine/view/item~Item view item} and all of its children to plain text.
 *
 * @param {module:engine/view/item~Item} viewItem View item to convert.
 * @returns {String} Plain text representation of `viewItem`.
 */
function viewToPlainText( viewItem ) {
	let text = '';

	if ( viewItem.is( '$text' ) || viewItem.is( '$textProxy' ) ) {
		// If item is `Text` or `TextProxy` simple take its text data.
		text = viewItem.data;
	} else if ( viewItem.is( 'element', 'img' ) && viewItem.hasAttribute( 'alt' ) ) {
		// Special case for images - use alt attribute if it is provided.
		text = viewItem.getAttribute( 'alt' );
	} else if ( viewItem.is( 'element', 'br' ) ) {
		// A soft break should be converted into a single line break (#8045).
		text = '\n';
	} else {
		// Other elements are document fragments, attribute elements or container elements.
		// They don't have their own text value, so convert their children.
		let prev = null;

		for ( const child of viewItem.getChildren() ) {
			const childText = viewToPlainText( child );

			// Separate container element children with one or more new-line characters.
			if ( prev && ( prev.is( 'containerElement' ) || child.is( 'containerElement' ) ) ) {
				if ( smallPaddingElements.includes( prev.name ) || smallPaddingElements.includes( child.name ) ) {
					text += '\n';
				} else {
					text += '\n\n';
				}
			}

			text += childText;
			prev = child;
		}
	}

	return text;
}


export default SMSForm;
